import { useMutation, useQueryClient } from '@tanstack/react-query';
import authAxios from '@services/authAxios';
import { useNavigate } from 'react-router-dom';
import { useSnackbarNotification } from '@store/snackbarNotification';
import { usePromotionClaim } from '../state/usePromotionClaim';
import { useEditDetail } from '../state/useEditDetail';
import { fileDownloader } from '@helpers/utils';
import { useConfirmModal } from '@store/useConfirmModal';
import { SuccessIcon2 } from '@assets/icons';

const useMutateCPList = () => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const state = usePromotionClaim();
    const stateEdit = useEditDetail();
    const stateConfirm = useConfirmModal();

    const { onChangeSnackbarNotification } = useSnackbarNotification();

    const createCP = useMutation({
        mutationFn: async (payload) => {
            const { data } = await authAxios.post('/api/v1/promotion-claim', payload);
            return data;
        },
        onSuccess: (data) => {
            navigate(`detail/${data.data.id}`);
            onChangeSnackbarNotification({
                status: 'success',
                title: 'Sukses Create Promotion Claim',
                description: 'Lanjutkan proses dengan melengkapi daftar SKU dan customer yang eligible.',
                duration: 3,
            });
        },
        onError: (error) => {
            onChangeSnackbarNotification({
                status: 'error',
                description: 'Gagal Create Promotion Claim!',
                duration: 3,
            });
        },
        onSettled: () => {
            state.reset();
        },
    });

    const updateCP = useMutation({
        mutationFn: async ({ id, payload }) => {
            const { data } = await authAxios.patch(`/api/v1/promotion-claim/edit/${id}`, payload);
            return data;
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries(['promotion-claim']);
            onChangeSnackbarNotification({
                status: 'success',
                description: 'Sukses mengedit promotion claim!',
                duration: 3,
            });
        },
        onError: (error) => {
            onChangeSnackbarNotification({
                status: 'error',
                description: 'Gagal mengedit promotion claim!',
                duration: 3,
            });
        },
    });

    const deleteCP = useMutation({
        mutationFn: async (id) => {
            const { data } = await authAxios.delete(`/api/v1/promotion-claim/${id}`);
            return data;
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries(['promotion-claim']);
            onChangeSnackbarNotification({
                status: 'success',
                description: 'Sukses menghapus promotion claim!',
                duration: 3,
            });
        },
        onError: (error) => {
            onChangeSnackbarNotification({
                status: 'error',
                description: 'Gagal menghapus promotion claim!',
                duration: 3,
            });
        },
        onSettled: () => {
            state.reset();
        },
    });

    const requestApproval = useMutation({
        mutationFn: async (id) => {
            onChangeSnackbarNotification({
                status: 'info',
                description: 'Request Approval Sedang Dikirimkan.',
                alwaysShow: true,
            });
            const { data } = await authAxios.post(`/api/v1/promotion-claim/${id}/request`);
            return data;
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries(['promotion-claim']);
            onChangeSnackbarNotification({
                status: 'success',
                title: 'Request Approval Sukses Dikirimkan',
                description: 'Mohon menunggu approval dari Business Leader.',
                duration: 3,
            });
        },
        onError: (error) => {
            onChangeSnackbarNotification({
                status: 'error',
                description: 'Gagal Request Approval!',
                duration: 3,
            });
        },
    });

    const approveRequest = useMutation({
        mutationFn: async (id) => {
            const { data } = await authAxios.post(`/api/v1/promotion-claim/${id}/approve`);
            return data;
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries(['promotion-claim']);
            onChangeSnackbarNotification({
                status: 'success',
                title: 'Approval Telah Diberikan',
                description: 'Program promotion claim telah dapat dijalankan.',
                duration: 3,
            });
        },
        onError: (error) => {
            const description = error?.response?.data?.message || 'Gagal Approve Request!';
            onChangeSnackbarNotification({
                status: 'error',
                description,
                duration: 3,
            });
        },
    });

    const rejectRequest = useMutation({
        mutationFn: async ({ id, payload }) => {
            const { data } = await authAxios.post(`/api/v1/promotion-claim/${id}/reject`, payload);
            return data;
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries(['promotion-claim']);
            onChangeSnackbarNotification({
                status: 'error',
                title: 'Promotion Claim Telah Ditolak',
                description: 'Program promotion claim tidak dapat dijalankan.',
                duration: 3,
            });
        },
        onError: (error) => {
            const description = error?.response?.data?.message || 'Gagal Reject Request!';
            onChangeSnackbarNotification({
                status: 'error',
                description,
                duration: 3,
            });
        },
    });

    const claimPromoAllCust = useMutation({
        mutationFn: async ({ idPromo }) => {
            const { data } = await authAxios.post(`/api/v1/promotion-claim/${idPromo}/claim`);
            return data;
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries(['promotion-claim']);
            onChangeSnackbarNotification({
                status: 'success',
                title: 'Sukses Melakukan Claim Promo',
                description: 'Untuk semua customer yang terdaftar.',
                duration: 3,
            });
            stateEdit.set({ claimPromo: null });
        },
        onError: async (error) => {
            if (error.response && error.response.data instanceof Blob) {
                const text = await error.response.data.text();
                try {
                    const json = JSON.parse(text);
                    onChangeSnackbarNotification({
                        status: 'error',
                        title: 'Gagal download data!',
                        description: `${json?.message}`,
                        duration: 3,
                    });
                } catch {
                    console.error('Error (not JSON):', text);
                }
            } else {
                const msg = error?.response?.data?.message;
                onChangeSnackbarNotification({
                    status: 'error',
                    title: 'Gagal download data!',
                    description: `${msg}`,
                    duration: 3,
                });
            }

            stateEdit.set({ claimPromo: null });
        },
    });

    const claimPromoByCust = useMutation({
        mutationFn: async ({ id }) => {
            const { data } = await authAxios.post(`/api/v1/promotion-claim/${id.promo}/claim?customer_id=${id.cust}`);
            return data;
        },
        onSuccess: async (data) => {
            queryClient.invalidateQueries(['promotion-claim']);
            onChangeSnackbarNotification({
                status: 'success',
                title: 'Sukses Melakukan Claim Promo',
                description: `Untuk ${stateEdit?.claimPromo?.name}.`,
                duration: 3,
            });

            await fileDownloader({
                url: `/api/v1/promotion-claim/${stateEdit?.claimPromo?.idCust}/claim/customer-export`,
                fileName: `Data Claim Promotion ${stateEdit?.claimPromo?.name}.pdf`,
            });
            stateEdit.set({ claimPromo: null });
        },
        onError: async (error) => {
            if (error.response && error.response.data instanceof Blob) {
                const text = await error.response.data.text();
                try {
                    const json = JSON.parse(text);
                    onChangeSnackbarNotification({
                        status: 'error',
                        title: 'Gagal download data!',
                        description: `${json?.message}`,
                        duration: 3,
                    });
                } catch {
                    console.error('Error (not JSON):', text);
                }
            } else {
                const msg = error?.response?.data?.message;
                onChangeSnackbarNotification({
                    status: 'error',
                    title: 'Gagal download data!',
                    description: `${msg}`,
                    duration: 3,
                });
            }

            stateEdit.set({ claimPromo: null });
        },
    });

    const completePromoByCust = useMutation({
        mutationFn: async ({ id }) => {
            const { data } = await authAxios.post(
                `/api/v1/promotion-claim/${id.promo}/complete?customer_id=${id.cust}`
            );
            return data;
        },
        onSuccess: async (data) => {
            queryClient.invalidateQueries(['promotion-claim']);
            setTimeout(() => {
                stateConfirm.set({
                    isOpen: true,
                    title: 'Process Completed',
                    desc: 'Proses Promotion Claim untuk customer ini telah diselesaikan.',
                    isShowBtnNo: false,
                    btnLabelYes: 'Tutup',
                    ConfirmationStateImage: SuccessIcon2,
                    onSubmit: () => {
                        stateEdit.set({ claimPromo: null });
                    },
                });
            }, 1000);
        },
        onError: async (error) => {
            const msg = error?.response?.data?.message;
            onChangeSnackbarNotification({
                status: 'error',
                title: 'Error!',
                description: `${msg}`,
                duration: 3,
            });

            stateEdit.set({ claimPromo: null });
        },
    });

    const completePromoAllCust = useMutation({
        mutationFn: async ({ idPromo }) => {
            const { data } = await authAxios.post(`/api/v1/promotion-claim/${idPromo}/complete`);
            return data;
        },
        onSuccess: async (data) => {
            queryClient.invalidateQueries(['promotion-claim']);
            stateConfirm.set({
                isOpen: true,
                title: 'All Process Completed',
                desc: 'Semua proses Promotion Claim telah diselesaikan.',
                isShowBtnNo: false,
                btnLabelYes: 'Tutup',
                ConfirmationStateImage: SuccessIcon2,
                onSubmit: () => {
                    stateEdit.set({ claimPromo: null });
                },
            });
        },
        onError: async (error) => {
            const msg = error?.response?.data?.message;
            if (msg) {
                onChangeSnackbarNotification({
                    status: 'error',
                    title: 'Error!',
                    description: `${msg}`,
                    duration: 3,
                });
            } else {
                onChangeSnackbarNotification({
                    status: 'error',
                    title: 'Error!',
                    description: `Server Error!`,
                    duration: 3,
                });
            }

            stateEdit.set({ claimPromo: null });
        },
    });

    return {
        createCP,
        updateCP,
        deleteCP,
        requestApproval,
        approveRequest,
        rejectRequest,
        claimPromoAllCust,
        claimPromoByCust,
        completePromoByCust,
        completePromoAllCust,
    };
};

export default useMutateCPList;
